import React, { useId } from "react";
import { HiArrowTrendingDown, HiArrowTrendingUp, HiMinus } from "react-icons/hi2";
import { useCountUp } from "./hooks";
import { deltaOf } from "./format";

/** Tiny trend line: recessive gray, current point in the accent. */
export function Sparkline({ values = [], width = 120, height = 36 }) {
  const id = useId();
  if (!values.length) return null;
  const max = Math.max(...values), min = Math.min(0, ...values), span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const pts = values.map((v, i) => [i * step, height - 3 - ((v - min) / span) * (height - 8)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  const area = `${line}L${width},${height}L0,${height}Z`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg className="dk-sparkline" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`${id}-g`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--dk-accent)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--dk-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id}-g)`} />
      <path d={line} className="dk-sparkline__line" />
      <circle cx={lx} cy={ly} r="3.5" className="dk-sparkline__dot" />
    </svg>
  );
}

/**
 * Change vs the previous period. Colour shows whether the change is good
 * (always paired with an arrow and the signed number, never colour alone).
 */
export function Delta({ current, previous, previousLabel, goodWhen = "up" }) {
  const d = deltaOf(current, previous);
  if (!d) return <span className="dk-delta dk-delta--flat">No activity in either period</span>;
  const tone = goodWhen === "neutral" || d.direction === "flat" ? "flat" : (d.direction === "up") === (goodWhen === "up") ? "good" : "bad";
  const Icon = d.direction === "up" ? HiArrowTrendingUp : d.direction === "down" ? HiArrowTrendingDown : HiMinus;
  const text = d.isNew ? "New" : `${d.pct > 0 ? "+" : ""}${Math.abs(d.pct) >= 100 ? Math.round(d.pct) : d.pct.toFixed(1)}%`;
  return (
    <span className={`dk-delta dk-delta--${tone}`}>
      <span className="dk-delta__chip">
        <Icon aria-hidden="true" size={14} />
        {text}
      </span>
      <span className="dk-delta__label">vs {previousLabel}</span>
    </span>
  );
}

/**
 * KPI tile: label, animated value, optional delta and sparkline.
 * `format` renders the numeric value (count-up animates the number).
 */
export function StatTile({ label, value, format = (v) => v, icon: Icon, current, previous, previousLabel, goodWhen, trend, hint, index = 0 }) {
  const numeric = typeof value === "number" && Number.isFinite(value);
  const animated = useCountUp(numeric ? value : 0);
  return (
    <article className="dk-tile dk-rise" style={{ "--i": index }}>
      <div className="dk-tile__top">
        <p className="dk-tile__label">{label}</p>
        {Icon && (
          <span className="dk-tile__icon" aria-hidden="true">
            <Icon size={18} />
          </span>
        )}
      </div>
      <div className="dk-tile__middle">
        <p className="dk-tile__value" aria-label={`${label}: ${numeric ? format(value) : value ?? "—"}`}>
          {numeric ? format(animated) : value ?? "—"}
        </p>
        {trend?.length > 1 && <Sparkline values={trend} />}
      </div>
      <div className="dk-tile__bottom">
        {current !== undefined ? <Delta current={current} previous={previous} previousLabel={previousLabel} goodWhen={goodWhen} /> : hint ? <span className="dk-tile__hint">{hint}</span> : null}
      </div>
    </article>
  );
}
